import React, { useEffect, useState } from 'react';
import api from '../api/questions';
import { getQuestions } from '../api/questions';

const formatNumber = (value, decimals = 0) => {
  if (value === undefined || value === null) {
    console.warn('Valeur non définie ou nulle passée à formatNumber');
    return 'N/A'; // Retourne "N/A" par défaut si la valeur est invalide
  }
  return Number(value.toFixed(decimals));
};

const Quiz = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [shuffledChoices, setShuffledChoices] = useState([]);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);
  const [userElo, setUserElo] = useState(null);
  const [userRD, setUserRD] = useState(null);
  const [quizFinished, setQuizFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [lastAnswerResult, setLastAnswerResult] = useState(null); // Nouvel état pour afficher le résultat de la dernière réponse

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await api.get('/api/auth/me');
        setUserElo(response.data.elo);
        setUserRD(response.data.RD);
      } catch (error) {
        console.error('Erreur lors de la récupération du profil utilisateur :', error.message);
      }
    };

    const fetchQuestions = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        console.error('Aucun ID utilisateur trouvé. Assurez-vous que l’utilisateur est connecté.');
        return;
      }

      try {
        const data = await getQuestions(userId);
        setQuestions(data);
      } catch (error) {
        console.error('Erreur lors de la récupération des questions :', error.message);
      }
    };

    fetchUserProfile();
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (userElo !== null && questions.length > 0 && currentQuestion === null) {
      selectNextQuestion(questions);
    }
  }, [userElo, questions, currentQuestion]);

  const selectNextQuestion = (allQuestions) => {
    if (!userElo || !userRD) {
      console.warn('Elo ou RD utilisateur non chargés.');
      return;
    }

    console.log('Elo utilisateur :', userElo);

    const today = new Date().toISOString().split('T')[0];
    const performanceByTag = localStorage.getItem('performanceByTag')
      ? JSON.parse(localStorage.getItem('performanceByTag'))
      : {};

    const askedToday = new Set();
    for (const tag in performanceByTag) {
      performanceByTag[tag]
        .filter((entry) => entry.date === today)
        .forEach((entry) => askedToday.add(entry.questionId));
    }

    const availableQuestions = allQuestions.filter((q) => !askedToday.has(q.id));

    console.log(`Nombre total de questions (avant filtrage) : ${allQuestions.length}`);
    console.log(`Nombre de questions disponibles aujourd\'hui (après filtrage) : ${availableQuestions.length}`);

    if (availableQuestions.length === 0) {
      console.warn(
        'Toutes les questions ont été posées aujourd\'hui ou il n\'y a plus de questions disponibles.',
        { askedToday, performanceByTag }
      );
      setQuizFinished(true);
      return;
    }

    const randomIndex = Math.floor(Math.random() * availableQuestions.length);
    const nextQuestion = availableQuestions[randomIndex];

    randomizeChoices(nextQuestion);
    setCurrentQuestion(nextQuestion);
    setTimeLeft(60);
  };

  const randomizeChoices = (question) => {
    const choices = [...question.choices];
    const correctAnswer = choices[question.correctAnswer];

    const shuffled = choices
      .map((choice) => ({ choice, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map((item) => item.choice);

    setShuffledChoices(shuffled);
    setCorrectAnswerIndex(shuffled.indexOf(correctAnswer));
  };

  useEffect(() => {
    if (timeLeft === 0) {
      handleAnswer(false);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAnswer = async (selectedAnswer) => {
    const userId = localStorage.getItem('userId');
  
    if (!userId) {
      console.error('Erreur : Utilisateur non connecté');
      return;
    }
  
    try {
      const response = await api.post('/api/questions/update-elo', {
        userId,
        questionId: currentQuestion?.id,
        selectedAnswer, // Envoie la réponse sélectionnée au backend
      });
  
      // Mettre à jour le stockage local avec les nouvelles performances
      localStorage.setItem('performanceByTag', JSON.stringify(response.data.performanceByTag));
  
      // Met à jour l'état du score Elo et de l'indicateur de fiabilité
      setUserElo(response.data.user.elo);
      setUserRD(response.data.user.RD);
  
      // Affiche si la réponse était correcte ou incorrecte
      setLastAnswerResult(response.data.correct ? 'correcte' : 'incorrecte');
  
      // Prépare la prochaine question ou termine le quiz
      if (questions.length > 0) {
        setCurrentQuestion(null);
      } else {
        setQuizFinished(true);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API :', error.message);
    }
  };  

  if (userElo === null || !questions.length) {
    return <p>Chargement des données...</p>;
  }

  if (quizFinished) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Quiz terminé !</h1>
        <p>Merci d'avoir joué. Votre score Elo actuel est : {formatNumber(userElo)}</p>
      </div>
    );
  }

  if (!currentQuestion) return null;

  return (
    <div style={{ textAlign: 'center', marginTop: '50px' }}>
      <h1>Question</h1>
      <p>{currentQuestion.content}</p>
      <p>
        <strong>Question :</strong> ID : {currentQuestion?.id || 'Inconnu'}, 
        Elo : {currentQuestion?.elo !== undefined ? formatNumber(currentQuestion.elo) : 'Inconnu'}, 
        RD : {currentQuestion?.RD !== undefined ? formatNumber(currentQuestion.RD) : 'Inconnu'}
      </p>
      <p>
        <strong>Utilisateur :</strong> Email : {localStorage.getItem('email') || 'Non connecté'}, 
        Elo : {userElo !== null ? formatNumber(userElo) : 'Chargement...'}, 
        RD : {userRD !== null ? formatNumber(userRD) : 'Chargement...'}
      </p>
      {shuffledChoices.map((choice, index) => (
        <button key={index} onClick={() => handleAnswer(choice)}>
          {choice}
        </button>
      ))}

      {lastAnswerResult && (
        <p style={{ marginTop: '20px', fontWeight: 'bold' }}>
          Votre dernière réponse était <span style={{ color: lastAnswerResult === 'correcte' ? 'green' : 'red' }}>{lastAnswerResult}</span>.
        </p>
      )}
      <div style={{ margin: '20px', fontSize: '20px', color: timeLeft <= 10 ? 'red' : 'black' }}>
        Temps restant : {timeLeft}s
      </div>
    </div>
  );
};

export default Quiz;
